import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

type Props = {
  endpointId?: string;
};

const examples: Record<string, { title: string; endpoint: string; curl: string; json: string; body?: string; sections?: { id: string; title: string }[] }> = {
  'chat-completions': {
    title: 'Chat Completions',
    endpoint: 'POST /v1/chat/completions',
    curl: `curl --location 'http://localhost:8080/api/scribing/chat-api/v1/chat/completions' \\
--header 'Authorization: Bearer sk_2dsdrBZSDPe2QQ0L6smmOxM8WasYR7Mw7R' \\
--header 'Content-Type: application/json' \\
--data '{
      "messages": [
          {
              "role": "system",
              "content": "You are a helpful assistant."
          },
          {
              "role": "user",
              "content": "What is the capital of india?"
          }
      ],
      "model": "gpt-4o",
      "temperature": 0.7,
      "max_tokens": 100
  }'`,
    json: `{
      "messages": [
          {
              "role": "system",
              "content": "You are a helpful assistant."
          },
          {
              "role": "user",
              "content": "What is the capital of india?"
          }
      ],
      "model": "gpt-4o",
      "temperature": 0.7,
      "max_tokens": 100
  }`,
    body: `# 📘 Scribing API — Chat Completions (OpenAI Compatible)

OpenAI-compatible **chat completions API** for AI-powered conversations.
Drop-in replacement for OpenAI's \`/v1/chat/completions\`.

---

## 🔑 Base URL
\`\`\`
http://localhost:8080/api/scribing/chat-api
\`\`\`

## 🔐 Authentication
- Type: **Bearer Token (API Key)**
- Format:
  \`\`\`http
  Authorization: Bearer sk_xxxxx
  \`\`\`
- Requirement: Developer account with an active API key

---

## 📡 Endpoints

### 1. Create Chat Completion
\`POST /v1/chat/completions\`

Creates a chat completion response using an AI model.

#### Request Headers
\`\`\`http
Authorization: Bearer sk_2YrBZSDPe2QQ0L6smmOxM8WasYR7Mw7R
Content-Type: application/json
\`\`\`

#### Request Body
\`\`\`json
{
  "messages": [
    { "role": "system", "content": "You are a helpful assistant." },
    { "role": "user", "content": "What is the capital of India?" }
  ],
  "model": "gpt-4o",
  "temperature": 0.7,
  "max_tokens": 100
}
\`\`\`

#### Parameters

| Parameter    | Type   | Required | Default         | Description                         |
|--------------|--------|----------|-----------------|-------------------------------------|
| \`messages\`   | Array  | ✅ Yes   | -               | List of conversation messages       |
| \`model\`      | String | ❌ No    | \`gpt-3.5-turbo\` | AI model to use                     |
| \`temperature\`| Number | ❌ No    | 1.0             | Randomness (0.0 – 2.0)              |
| \`max_tokens\` | Number | ❌ No    | 150             | Maximum tokens in the response      |

#### Message Object

| Field    | Type   | Required | Description                           |
|----------|--------|----------|---------------------------------------|
| \`role\`   | String | ✅ Yes   | Role: \`system\`, \`user\`, or \`assistant\`|
| \`content\`| String | ✅ Yes   | Message content                       |

#### Example Response
\`\`\`json
{
  "id": "chatcmpl-abc123",
  "object": "chat.completion",
  "created": 1677610602,
  "model": "gpt-4o",
  "choices": [
    {
      "index": 0,
      "message": {
        "role": "assistant",
        "content": "The capital of India is New Delhi."
      },
      "finish_reason": "stop"
    }
  ],
  "usage": {
    "prompt_tokens": 25,
    "completion_tokens": 8,
    "total_tokens": 33
  }
}
\`\`\`

---

### 2. List Models
\`GET /v1/models\`

Lists available AI models.

#### Example Response
\`\`\`json
{
  "object": "list",
  "data": [
    {
      "id": "gpt-4o",
      "object": "model",
      "created": 1677610602,
      "owned_by": "openai"
    },
    {
      "id": "gpt-3.5-turbo",
      "object": "model",
      "created": 1677610602,
      "owned_by": "openai"
    },
    {
      "id": "gemini-2.0-flash",
      "object": "model",
      "created": 1677610602,
      "owned_by": "google"
    }
  ]
}
\`\`\`

---

## ⚠️ Error Responses

### 401 Unauthorized
\`\`\`json
{
  "detail": "API key required"
}
\`\`\`

### 403 Forbidden
\`\`\`json
{
  "detail": "Developer account required"
}
\`\`\`

### 500 Internal Server Error (Fallback)
\`\`\`json
{
  "id": "chatcmpl-fallback123",
  "object": "chat.completion",
  "created": 1677610602,
  "model": "gpt-4o",
  "choices": [
    {
      "index": 0,
      "message": {
        "role": "assistant",
        "content": "I apologize, but I'm currently experiencing technical difficulties. Please try again later."
      },
      "finish_reason": "stop"
    }
  ],
  "usage": {
    "prompt_tokens": 25,
    "completion_tokens": 20,
    "total_tokens": 45
  }
}
\`\`\`

---

## 💻 Implementation Examples

### JavaScript (Fetch)
\`\`\`javascript
const response = await fetch('http://localhost:8080/api/scribing/chat-api/v1/chat/completions', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer sk_2YrBZSDPe2QQ0L6smmOxM8WasYR7Mw7R',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    messages: [
      { role: 'system', content: 'You are a helpful assistant.' },
      { role: 'user', content: 'What is the capital of India?' }
    ],
    model: 'gpt-4o',
    temperature: 0.7,
    max_tokens: 100
  })
});

const data = await response.json();
console.log(data.choices[0].message.content);
\`\`\`

### Python (requests)
\`\`\`python
import requests

response = requests.post(
    'http://localhost:8080/api/scribing/chat-api/v1/chat/completions',
    headers={
        'Authorization': 'Bearer sk_2YrBZSDPe2QQ0L6smmOxM8WasYR7Mw7R',
        'Content-Type': 'application/json'
    },
    json={
        'messages': [
            {'role': 'system', 'content': 'You are a helpful assistant.'},
            {'role': 'user', 'content': 'What is the capital of India?'}
        ],
        'model': 'gpt-4o',
        'temperature': 0.7,
        'max_tokens': 100
    }
)

data = response.json()
print(data['choices'][0]['message']['content'])
\`\`\`

---

## ✨ Features

- **🔄 Fallback Mechanism:** Returns graceful error response if AI service fails
- **📊 Usage Tracking:** Token usage and cost tracking for billing
- **📝 Request Logging:** All requests logged via middleware
- **🔗 OpenAI Compatibility:** Drop-in replacement for OpenAI chat completions`,
    sections: [
      { id: 'overview', title: 'Overview' }
    ]
  }
};

export default function ApiReference({ endpointId = 'chat-completions' }: Props) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const data = examples[endpointId] || examples['chat-completions'];

  const copy = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      window.setTimeout(() => setCopied(null), 1600);
    } catch (e) {
      console.error('Copy failed', e);
    }
  };

  useEffect(() => { window.scrollTo(0, 0); }, [endpointId]);

  return (
    <div className="h-full flex bg-slate-50 text-slate-900 overflow-hidden api-reference-container">
      {/* Left Nav */}
      <aside className="w-72 bg-white border-r border-slate-200 flex flex-col h-full shadow-sm">
        <div className="p-6 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <h4 className="text-lg font-semibold text-slate-900">API Documentation</h4>
              <p className="text-sm text-slate-500">Developer Reference</p>
            </div>
          </div>
        </div>

        <div className="p-4 border-b border-slate-100 flex-shrink-0">
          <div className="relative">
            <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search documentation..."
              className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <style>{`
          /* Override parent container overflow for docs */
          body:has(.api-reference-container) .flex-1.overflow-auto {
            overflow: hidden !important;
          }

          /* custom thin scrollbar for docs sidebar */
          .docs-sidebar::-webkit-scrollbar { width: 8px; }
          .docs-sidebar::-webkit-scrollbar-track { background: #f9fafb; }
          .docs-sidebar::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 999px; }
          .docs-sidebar { scrollbar-width: thin; scrollbar-color: #d1d5db #f9fafb; }

          /* custom scrollbar for main content */
          .main-content::-webkit-scrollbar { width: 8px; }
          .main-content::-webkit-scrollbar-track { background: #f9fafb; }
          .main-content::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 999px; }
          .main-content { scrollbar-width: thin; scrollbar-color: #d1d5db #f9fafb; }

          /* custom scrollbar for right sidebar */
          .right-sidebar::-webkit-scrollbar { width: 8px; }
          .right-sidebar::-webkit-scrollbar-track { background: #f9fafb; }
          .right-sidebar::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 999px; }
          .right-sidebar { scrollbar-width: thin; scrollbar-color: #d1d5db #f9fafb; }
        `}</style>

        <nav className="flex-1 overflow-y-auto p-4 docs-sidebar min-h-0">
          <div className="space-y-1">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 px-3">
              Endpoints
            </div>
            {Object.keys(examples)
              .filter(key => examples[key].title.toLowerCase().includes(query.toLowerCase()))
              .map(key => (
                <button
                  key={key}
                  onClick={() => navigate(`/docs/${key}`)}
                  className={`group flex items-center w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                    key === endpointId
                      ? 'bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 border-l-4 border-blue-500'
                      : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  <div className={`w-2 h-2 rounded-full mr-3 ${
                    key === endpointId ? 'bg-blue-500' : 'bg-slate-300 group-hover:bg-slate-400'
                  }`} />
                  {examples[key].title}
                  {key === endpointId && (
                    <svg className="w-4 h-4 ml-auto text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              ))}
          </div>
        </nav>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto main-content h-full min-h-0 bg-white" style={{ maxHeight: '100vh' }}>
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-slate-50 to-blue-50 border-b border-slate-200 px-8 py-8">
          <div className="max-w-4xl">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="flex items-center space-x-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        POST
                      </span>
                      <span className="text-slate-500">/v1/chat/completions</span>
                    </div>
                  </div>
                  <h1 className="text-4xl font-bold mb-3 text-slate-900 tracking-tight">{data.title}</h1>
                  <p className="text-xl text-slate-600 leading-relaxed">{data.endpoint}</p>
                </div>
              </div>
            </div>
          </div>

        {/* Content */}
        <div className="px-8 py-8 pb-16">
          <div className="max-w-4xl">
              {copied && (
                <div className="fixed top-6 right-6 bg-emerald-500 text-white text-sm px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 z-50">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>Copied to clipboard!</span>
                </div>
              )}

          {/* Documentation Content */}
          <div className="prose prose-slate max-w-none">
            {data.body && (
              <div className="space-y-6">
                {/* Render the markdown content in a structured way */}
                <div className="space-y-8">
                  {/* Title */}
                  <div>
                    <h1 className="text-3xl font-bold text-slate-900 mb-3">
                      Chat Completions API
                    </h1>
                    <p className="text-xl text-slate-600 leading-relaxed">
                      OpenAI-compatible <strong>chat completions API</strong> for AI-powered conversations.<br />
                      Drop-in replacement for OpenAI's <code className="bg-slate-100 text-slate-800 px-2 py-0.5 rounded text-sm font-mono">/v1/chat/completions</code>.
                    </p>
                  </div>

                  <hr className="border-slate-200" />

                  {/* Base URL */}
                  <div>
                    <h2 className="text-2xl font-semibold text-slate-900 mb-4 flex items-center">
                      🔑 Base URL
                    </h2>
                    <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 shadow-lg">
                      <code className="text-green-400 font-mono">http://localhost:8080/api/scribing/chat-api</code>
                    </div>
                  </div>

                  {/* Authentication */}
                  <div>
                    <h2 className="text-2xl font-semibold text-slate-900 mb-4 flex items-center">
                      🔐 Authentication
                    </h2>
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <span className="text-blue-500 mr-2 mt-1">•</span>
                        Type: <strong>Bearer Token (API Key)</strong>
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-500 mr-2 mt-1">•</span>
                        Format:
                        <div className="ml-2 bg-gray-900 border border-gray-700 rounded px-3 py-1 text-green-400 font-mono text-sm">
                          Authorization: Bearer sk_xxxxx
                        </div>
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-500 mr-2 mt-1">•</span>
                        Requirement: Developer account with an active API key
                      </li>
                    </ul>
                  </div>

                  <hr className="border-slate-200" />

                  {/* Endpoints */}
                  <div>
                    <h2 className="text-2xl font-semibold text-slate-900 mb-6">
                      Endpoints
                    </h2>

                    {/* Chat Completions Endpoint */}
                    <div className="mb-8">
                      <h3 className="text-xl font-medium text-slate-800 mb-3">Create Chat Completion</h3>
                      <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium inline-block mb-4">
                        POST /v1/chat/completions
                      </div>
                      <p className="text-slate-700 mb-4">Creates a chat completion response using an AI model.</p>

                      {/* Request Headers */}
                      <h4 className="text-lg font-medium text-slate-700 mb-2">Request Headers</h4>
                      <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 mb-4">
                        <pre className="text-orange-300 font-mono text-sm">Authorization: Bearer sk_2YrBZSDPe2QQ0L6smmOxM8WasYR7Mw7R
Content-Type: application/json</pre>
                      </div>

                      {/* Request Body */}
                      <h4 className="text-lg font-medium text-slate-700 mb-2">Request Body</h4>
                      <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 mb-6">
                        <pre className="text-blue-300 font-mono text-sm">{`{
  "messages": [
    { "role": "system", "content": "You are a helpful assistant." },
    { "role": "user", "content": "What is the capital of India?" }
  ],
  "model": "gpt-4o",
  "temperature": 0.7,
  "max_tokens": 100
}`}</pre>
                      </div>

                      {/* Parameters Table */}
                      <h4 className="text-lg font-medium text-slate-700 mb-3">Parameters</h4>
                      <div className="overflow-x-auto mb-6">
                        <table className="min-w-full bg-white border border-slate-200 rounded-lg">
                          <thead>
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider bg-slate-50 border-b border-slate-200">Parameter</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider bg-slate-50 border-b border-slate-200">Type</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider bg-slate-50 border-b border-slate-200">Required</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider bg-slate-50 border-b border-slate-200">Default</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider bg-slate-50 border-b border-slate-200">Description</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="px-4 py-3 text-sm text-slate-700"><code>messages</code></td>
                              <td className="px-4 py-3 text-sm text-slate-700">Array</td>
                              <td className="px-4 py-3 text-sm text-slate-700">✅ Yes</td>
                              <td className="px-4 py-3 text-sm text-slate-700">-</td>
                              <td className="px-4 py-3 text-sm text-slate-700">List of conversation messages</td>
                            </tr>
                            <tr className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="px-4 py-3 text-sm text-slate-700"><code>model</code></td>
                              <td className="px-4 py-3 text-sm text-slate-700">String</td>
                              <td className="px-4 py-3 text-sm text-slate-700">❌ No</td>
                              <td className="px-4 py-3 text-sm text-slate-700"><code>gpt-3.5-turbo</code></td>
                              <td className="px-4 py-3 text-sm text-slate-700">AI model to use</td>
                            </tr>
                            <tr className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="px-4 py-3 text-sm text-slate-700"><code>temperature</code></td>
                              <td className="px-4 py-3 text-sm text-slate-700">Number</td>
                              <td className="px-4 py-3 text-sm text-slate-700">❌ No</td>
                              <td className="px-4 py-3 text-sm text-slate-700">1.0</td>
                              <td className="px-4 py-3 text-sm text-slate-700">Randomness (0.0 – 2.0)</td>
                            </tr>
                            <tr className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="px-4 py-3 text-sm text-slate-700"><code>max_tokens</code></td>
                              <td className="px-4 py-3 text-sm text-slate-700">Number</td>
                              <td className="px-4 py-3 text-sm text-slate-700">❌ No</td>
                              <td className="px-4 py-3 text-sm text-slate-700">150</td>
                              <td className="px-4 py-3 text-sm text-slate-700">Maximum tokens in the response</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      {/* Example Response */}
                      <h4 className="text-lg font-medium text-slate-700 mb-2">Example Response</h4>
                      <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 mb-6">
                        <pre className="text-green-300 font-mono text-sm">{`{
  "id": "chatcmpl-abc123",
  "object": "chat.completion",
  "created": 1677610602,
  "model": "gpt-4o",
  "choices": [
    {
      "index": 0,
      "message": {
        "role": "assistant",
        "content": "The capital of India is New Delhi."
      },
      "finish_reason": "stop"
    }
  ],
  "usage": {
    "prompt_tokens": 25,
    "completion_tokens": 8,
    "total_tokens": 33
  }
}`}</pre>
                      </div>
                    </div>

                    {/* Models Endpoint */}
                    <div className="mb-8">
                      <h3 className="text-xl font-medium text-slate-800 mb-3">List Models</h3>
                      <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium inline-block mb-4">
                        GET /v1/models
                      </div>
                      <p className="text-slate-700 mb-4">Lists available AI models.</p>

                      <h4 className="text-lg font-medium text-slate-700 mb-2">Example Response</h4>
                      <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 mb-6">
                        <pre className="text-yellow-300 font-mono text-sm">{`{
  "object": "list",
  "data": [
    {
      "id": "gpt-4o",
      "object": "model",
      "created": 1677610602,
      "owned_by": "openai"
    },
    {
      "id": "gpt-3.5-turbo",
      "object": "model",
      "created": 1677610602,
      "owned_by": "openai"
    },
    {
      "id": "gemini-2.0-flash",
      "object": "model",
      "created": 1677610602,
      "owned_by": "google"
    }
  ]
}`}</pre>
                      </div>
                    </div>
                  </div>

                  <hr className="border-slate-200" />

                  {/* Features */}
                  <div>
                    <h2 className="text-2xl font-semibold text-slate-900 mb-4 flex items-center">
                      ✨ Features
                    </h2>
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <span className="text-blue-500 mr-2 mt-1">•</span>
                        <strong>🔄 Fallback Mechanism:</strong> Returns graceful error response if AI service fails
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-500 mr-2 mt-1">•</span>
                        <strong>📊 Usage Tracking:</strong> Token usage and cost tracking for billing
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-500 mr-2 mt-1">•</span>
                        <strong>📝 Request Logging:</strong> All requests logged via middleware
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-500 mr-2 mt-1">•</span>
                        <strong>🔗 OpenAI Compatibility:</strong> Drop-in replacement for OpenAI chat completions
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Interactive Examples Section */}
            <div className="mt-12 mb-12 space-y-6">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-8 h-8 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                </div>
                <h2 className="text-2xl font-semibold text-slate-900">Try it out</h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* cURL Example */}
                <div className="bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="px-6 py-4 border-b border-slate-100 bg-slate-50 rounded-t-xl">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-6 h-6 bg-orange-100 rounded-md flex items-center justify-center">
                          <svg className="w-4 h-4 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 4a1 1 0 011-1h12a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1V8zm2 2a1 1 0 000 2h.01a1 1 0 100-2H5zm3 0a1 1 0 000 2h3a1 1 0 100-2H8z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-slate-900">cURL Command</div>
                          <div className="text-xs text-slate-500">Ready to copy and run</div>
                        </div>
                      </div>
                      <button
                        onClick={() => copy(data.curl, 'curl')}
                        className="inline-flex items-center px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium rounded-md transition-colors"
                      >
                        <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        Copy
                      </button>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="bg-gray-900 border border-gray-700 rounded-lg p-4">
                      <pre className="text-sm font-mono text-green-400 whitespace-pre-wrap overflow-x-auto">{data.curl}</pre>
                    </div>
                  </div>
                </div>

                {/* JSON Example */}
                <div className="bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="px-6 py-4 border-b border-slate-100 bg-slate-50 rounded-t-xl">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-6 h-6 bg-blue-100 rounded-md flex items-center justify-center">
                          <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 4a1 1 0 011-1h12a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1V8zm2 2a1 1 0 000 2h.01a1 1 0 100-2H5zm3 0a1 1 0 000 2h3a1 1 0 100-2H8z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-slate-900">Request Body</div>
                          <div className="text-xs text-slate-500">JSON payload example</div>
                        </div>
                      </div>
                      <button
                        onClick={() => copy(data.json, 'json')}
                        className="inline-flex items-center px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium rounded-md transition-colors"
                      >
                        <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        Copy
                      </button>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="bg-gray-900 border border-gray-700 rounded-lg p-4">
                      <pre className="text-sm font-mono text-blue-300 whitespace-pre-wrap overflow-x-auto">{data.json}</pre>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </main>

      {/* Right column */}
      <aside className="w-80 bg-white border-l border-slate-200 flex flex-col h-full overflow-y-auto right-sidebar">
        <div className="p-6 pb-16">
        <div className="text-sm text-gray-600 mb-4">Just getting started?</div>
        <div className="bg-gray-50 rounded-lg p-3 mb-4">
          <div className="text-xs text-gray-600 mb-2">BASE URL</div>
          <div className="bg-gray-100 rounded px-3 py-2 font-mono text-sm text-gray-800">https://api.yourdomain.com</div>
        </div>

        <div className="text-xs text-gray-600 mb-2">API Info</div>
        <div className="space-y-2 mb-4 text-sm text-gray-700">
          <div><span className="font-semibold text-gray-800">Version:</span> <span className="text-gray-700">v1</span></div>
          <div><span className="font-semibold text-gray-800">Auth:</span> <span className="text-gray-700">Bearer token in Authorization header</span></div>
          <div><span className="font-semibold text-gray-800">Rate limits:</span> <span className="text-gray-700">60 req / minute (per key)</span></div>
          <div><span className="font-semibold text-gray-800">Content-Type:</span> <span className="text-gray-700">application/json</span></div>
          <div><span className="font-semibold text-gray-800">Support:</span> <a className="text-blue-600 hover:underline" href="mailto:support@yourdomain.com">support@yourdomain.com</a></div>
          <div><a href="/docs/api-status" className="text-xs text-blue-600 hover:underline">Check API status</a></div>
        </div>

        <div className="text-xs text-gray-500 mt-auto">By default, the docs demonstrate using curl. Use the API Info above to authenticate and learn limits.</div>
        </div>
      </aside>
    </div>
  );
}
